import type { WireTabNavigationBarContainer } from "@glide/fluent-components/dist/js/fluent-components-containers";
import * as React from "react";
import type { WireRenderer } from "../wire-renderer";
import * as Tabs from "@radix-ui/react-tabs";
import { getWireValue } from "../../wire-lib";
import { ValueChangeSource } from "@glide/wire";
import type { ComponentProps } from "react";
import { definedMap } from "@glideapps/ts-necessities";
import { TabBar } from "./wire-tab-navigation-bar";
import { useSectionStyle } from "../wire-container/wire-container";

type NavigationContainer = WireRenderer<WireTabNavigationBarContainer, { children: React.ReactNode }>;
type NavigationContainerProps = ComponentProps<NavigationContainer>;

export const TabNavigationContainerComponent: NavigationContainer = React.memo(p => {
    const { backend, paths, match, style } = p;

    const matchEditableValue = definedMap(match, state => {
        if (state === null) return undefined;
        return getWireValue(state, backend);
    });
    const [value, setValue] = matchEditableValue ?? [];

    const updateValue = React.useCallback(
        (newValue: string) => {
            setValue?.(newValue, ValueChangeSource.User);
        },
        [setValue]
    );

    React.useEffect(() => {
        if (value === "") {
            const initialValue = paths[0]?.path ?? "";
            if (initialValue !== "") {
                setValue?.(initialValue, ValueChangeSource.Internal);
            }
        }
    }, [paths, setValue, value]);

    const sectionStyle = useSectionStyle();

    const selectedValue = value ?? paths[0]?.path ?? "";

    return (
        <Tabs.Root
            className={sectionStyle}
            tw="[&.card]:py-6 [&.highlight]:py-6"
            onValueChange={updateValue}
            value={value ?? paths[0]?.path ?? ""}>
            <TabBar paths={paths} style={style} updateValue={updateValue} selectedValue={selectedValue} />
            <PathsContext.Provider value={paths}>{p.children}</PathsContext.Provider>
        </Tabs.Root>
    );
});

const PathsContext = React.createContext<NavigationContainerProps["paths"]>([]);

// This is used in pages-component-renderer for building the navigation container.
export const TabNavigationContainerContent = ({
    children,
    pathIndex,
}: {
    children: React.ReactNode;
    pathIndex: number;
}) => {
    const paths = React.useContext(PathsContext);
    const currentPath = paths[pathIndex];

    if (!currentPath?.visibilityCondition) return null;

    return <Tabs.Content value={currentPath?.path ?? ""}>{children}</Tabs.Content>;
};
